import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import {
  migrateLocalStorageToCloud,
  isMigrationComplete,
} from "./lib/data-migration";
import { SettingsProvider } from "./contexts/SettingsContext";
import { ThreadProvider } from "./contexts/ThreadContext";
import { ToastProvider } from "./contexts/ToastContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { DNDProvider } from "./contexts/DNDContext";
import { GatewayProvider } from "./contexts/GatewayContext";
import { FintheonAgentProvider } from "./contexts/FintheonAgentContext";
import { TeamPresenceProvider } from "./contexts/TeamPresenceContext";
import { RiskFlowProvider } from "./contexts/RiskFlowContext";
import { ContextBankProvider } from "./contexts/ContextBankContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { VoiceProvider } from "./contexts/VoiceContext";
import { ProxVoiceProvider } from "./contexts/ProxVoiceContext";
import { VoiceCaoDock } from "./components/voice/VoiceCaoDock";
import { PsychAssistSoundBridge } from "./components/psychassist/PsychAssistSoundBridge";
import { ERProvider } from "./contexts/ERContext";
import { MainLayout } from "./components/layout/MainLayout";
import SplashScreen from "./components/SplashScreen";
import { NotificationDeliveryBridge } from "./components/notifications/NotificationDeliveryBridge";
import { NotificationNativeDeliveryBridge } from "./components/notifications/NotificationNativeDeliveryBridge";
import { NotificationStackPresenter } from "./components/notifications/NotificationStackPresenter";
import { PreMarketReminder } from "./components/PreMarketReminder";
import { ApiErrorToastBridge } from "./components/ApiErrorToastBridge";
import { AiCreditToastBridge } from "./components/AiCreditToastBridge";
import { BackgroundTaskNotificationBridge } from "./components/BackgroundTaskNotificationBridge";
import { DeskPlanPlannerToastBridge } from "./components/DeskPlanPlannerToastBridge";
import { GrokAuthToastBridge } from "./components/GrokAuthToastBridge";
import { VersionChecker } from "./components/VersionChecker";
import { XBookmarksAutoSyncBridge } from "./components/XBookmarksAutoSyncBridge";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SystemStatusProvider } from "./contexts/SystemStatusContext";
import { migrateStorageKeys } from "./lib/storage-migration";
import { AuthShell } from "./components/auth/AuthShell";
import { DesktopAuthBridge } from "./components/auth/DesktopAuthBridge";
import { LoadingBootScreen } from "./components/loading/LoadingBootScreen";
import { ConsulControlCorners } from "./components/consul-control/ConsulControlCorners";
import { LazyProjectXSyncProvider } from "./components/projectx/LazyProjectXSyncProvider";
import { useConsulControlStatus } from "./hooks/useConsulControlStatus";
import { buildSurfaceCapabilities } from "./lib/surface-capabilities";
import { MainContentOverlayHost } from "./components/layout/MainContentOverlayHost";
import { LazyOpenClickyBridge } from "./components/openclicky/LazyOpenClickyBridge";
import { getRuntimeApiBase } from "./lib/runtime-api-base";
import { isDesktopDownloadEntry } from "./lib/web-auth-routing";
import { BetaDesktopDownloadPage } from "./components/beta/BetaDesktopDownloadPage";

// Run storage migration before any providers read localStorage
migrateStorageKeys();

function useAppInit(
  enabled: boolean,
  accessToken: string | null | undefined,
  onReady: () => void,
) {
  const initStatusRef = useRef<"idle" | "running" | "ready">("idle");

  useEffect(() => {
    if (!enabled) {
      initStatusRef.current = "idle";
      return;
    }
    if (initStatusRef.current === "ready") {
      onReady();
      return;
    }
    if (initStatusRef.current === "running") return;
    initStatusRef.current = "running";

    let cancelled = false;
    const API = getRuntimeApiBase();

    (async () => {
      await new Promise((r) => setTimeout(r, 400));
      if (cancelled) return;

      // Health is useful telemetry, but a healthy shell must not wait on it.
      try {
        await fetch(`${API}/health`, { signal: AbortSignal.timeout(2000) });
      } catch {
        /* offline-tolerant shell */
      }
      if (cancelled) return;

      // Local-state migration preserves user settings but is never a boot gate.
      if (!isMigrationComplete() && accessToken) {
        void migrateLocalStorageToCloud(accessToken).catch((err) => {
          console.warn("[Init] Migration skipped:", err);
        });
      }
      if (cancelled) return;

      await new Promise((r) => setTimeout(r, 300));
      if (cancelled) return;

      initStatusRef.current = "ready";
      onReady();
    })();

    return () => {
      cancelled = true;
      if (initStatusRef.current === "running") initStatusRef.current = "idle";
    };
  }, [accessToken, enabled, onReady]);
}

function isColdStart(): boolean {
  try {
    if (sessionStorage.getItem("fintheon:session-alive")) return false;
    sessionStorage.setItem("fintheon:session-alive", "1");
    return true;
  } catch {
    return true;
  }
}

function ConsulControlLayer() {
  const active = useConsulControlStatus();
  return <ConsulControlCorners active={active} />;
}

function AuthGate() {
  const { isAuthenticated, isLoading, session, signIn } = useAuth();
  const isDesktopAuthBridge = DesktopAuthBridge.shouldHandleCurrentPath();
  const [initComplete, setInitComplete] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 1600 : window.innerWidth,
  );
  const [showSplash] = useState(() => isColdStart());
  const authBypass = import.meta.env.VITE_BYPASS_AUTH === "true";
  const prototypeBypass =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("codex-prototype");
  const canEnterApp = isAuthenticated || authBypass || prototypeBypass;
  const desktopDownloadEntry = isDesktopDownloadEntry();
  const surfaceCapabilities = useMemo(
    () => buildSurfaceCapabilities(viewportWidth),
    [viewportWidth],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleInitReady = useCallback(() => {
    setInitComplete(true);
  }, []);

  useAppInit(
    !isDesktopAuthBridge && canEnterApp && !desktopDownloadEntry,
    session?.access_token,
    handleInitReady,
  );

  if (isDesktopAuthBridge) {
    return <DesktopAuthBridge />;
  }

  if (desktopDownloadEntry) return <BetaDesktopDownloadPage />;

  if (isLoading) {
    return <LoadingBootScreen phrase="Restoring session" />;
  }

  if (!canEnterApp) {
    return (
      <AuthShell onSignIn={signIn} isLoading={isLoading} intent="default" />
    );
  }

  return (
    <div style={{ opacity: 1 }}>
      {showSplash && <SplashScreen isReady={initComplete} />}
      <SettingsProvider>
        <DNDProvider>
          <NotificationsProvider>
            <ToastProvider>
              <GatewayProvider>
                <SystemStatusProvider>
                  <FintheonAgentProvider>
                    <TeamPresenceProvider>
                      <RiskFlowProvider>
                        <ContextBankProvider>
                          <ThreadProvider>
                            <VoiceProvider>
                              <ProxVoiceProvider>
                                <ERProvider>
                                  <div className="dark">
                                    {surfaceCapabilities.allowConsulControl && (
                                      <VoiceCaoDock />
                                    )}
                                    <style>{`
                  * {
                    scrollbar-width: thin;
                    scrollbar-color: var(--fintheon-accent) var(--fintheon-surface);
                  }

                  *::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                  }

                  *::-webkit-scrollbar-track {
                    background: var(--fintheon-surface);
                  }

                  *::-webkit-scrollbar-thumb {
                    background: var(--fintheon-accent);
                    border-radius: 4px;
                  }

                  *::-webkit-scrollbar-thumb:hover {
                    background: color-mix(in srgb, var(--fintheon-accent) 70%, white);
                  }

                  .scanline-overlay {
                    background: repeating-linear-gradient(
                      0deg,
                      color-mix(in srgb, var(--fintheon-accent) 3%, transparent) 0px,
                      color-mix(in srgb, var(--fintheon-accent) 3%, transparent) 1px,
                      transparent 1px,
                      transparent 2px
                    );
                    pointer-events: none;
                  }
                                  `}</style>
                                    <NotificationDeliveryBridge />
                                    <NotificationNativeDeliveryBridge />
                                    <ApiErrorToastBridge />
                                    <AiCreditToastBridge />
                                    <BackgroundTaskNotificationBridge />
                                    {surfaceCapabilities.allowPsychAssist && (
                                      <PsychAssistSoundBridge />
                                    )}
                                    <GrokAuthToastBridge />
                                    <XBookmarksAutoSyncBridge />
                                    <DeskPlanPlannerToastBridge />
                                    <LazyProjectXSyncProvider />
                                    <LazyOpenClickyBridge />
                                    <VersionChecker />
                                    <MainLayout />
                                    <MainContentOverlayHost />
                                    {surfaceCapabilities.allowVoiceAssistant && (
                                      <ConsulControlLayer />
                                    )}
                                    <NotificationStackPresenter
                                      mobile={surfaceCapabilities.isMobile}
                                    />
                                    <PreMarketReminder />
                                  </div>
                                </ERProvider>
                              </ProxVoiceProvider>
                            </VoiceProvider>
                          </ThreadProvider>
                        </ContextBankProvider>
                      </RiskFlowProvider>
                    </TeamPresenceProvider>
                  </FintheonAgentProvider>
                </SystemStatusProvider>
              </GatewayProvider>
            </ToastProvider>
          </NotificationsProvider>
        </DNDProvider>
      </SettingsProvider>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AuthGate />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
